import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { BookOpen, ChevronRight, ChevronLeft, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DiagnosticPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/diagnostic/questions`);
      setQuestions(response.data);
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Erreur lors du chargement des questions");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // Check if all questions answered
    const unanswered = questions.filter(q => !answers[q.id] || answers[q.id].trim() === "");
    if (unanswered.length > 0) {
      toast.error(`Veuillez répondre à toutes les questions (${unanswered.length} manquante(s))`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/diagnostic/submit`, {
        student_id: user.id,
        answers: answers
      });
      
      setResult(response.data);
      
      // Update user state
      const updatedUser = {
        ...user,
        diagnostic_completed: true,
        diagnostic_score: response.data.score
      };
      setUser(updatedUser);
      localStorage.setItem("philoprof_user", JSON.stringify(updatedUser));
      
      toast.success("Évaluation diagnostique terminée !");
    } catch (error) {
      console.error("Error submitting diagnostic:", error);
      toast.error("Erreur lors de la soumission");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-[#1A365D] animate-pulse mx-auto" />
          <p className="mt-4 text-[#64748B]">Chargement...</p>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] p-4" data-testid="diagnostic-result">
        <div className="max-w-2xl mx-auto pt-8">
          <Card className="card-sjh">
            <CardHeader className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#1A365D] flex items-center justify-center">
                <Check className="w-10 h-10 text-white" />
              </div>
              <CardTitle className="text-2xl text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Évaluation terminée !
              </CardTitle>
              <CardDescription className="text-lg">
                Votre score: <span className="font-bold text-[#D4AF37]">{result.score}%</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <div className="progress-container h-4">
                  <div 
                    className="progress-fill h-4" 
                    style={{ width: `${result.score}%` }}
                  />
                </div>
              </div>
              
              <div className="bg-[#F8F9FA] rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-[#1A365D] mb-2">Bilan de l'évaluation</h3>
                <p className="text-[#64748B] whitespace-pre-wrap">{result.evaluation}</p>
              </div>
              
              <Button 
                onClick={() => navigate("/dashboard")}
                className="w-full bg-[#1A365D] hover:bg-[#132745]"
                data-testid="go-dashboard-button"
              >
                Accéder à mon tableau de bord
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="diagnostic-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <span className="navbar-logo hidden sm:block">SophIA</span>
        </div>
        <span className="text-[#64748B]">
          Question {currentIndex + 1} / {questions.length}
        </span>
      </nav>

      <main className="px-4 py-8 max-w-3xl mx-auto">
        {/* Progress */}
        <div className="mb-8">
          <div className="progress-container">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <Card className="card-sjh mb-6">
          <CardHeader>
            <div className="inline-block px-3 py-1 bg-[#D4AF37] text-[#0F172A] text-sm rounded mb-2">
              {currentQuestion?.notion}
            </div>
            <CardTitle className="text-xl text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
              {currentQuestion?.question}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={answers[currentQuestion?.id] || ""}
              onChange={(e) => handleAnswerChange(currentQuestion?.id, e.target.value)}
              placeholder="Rédigez votre réponse ici..."
              className="min-h-[200px] border-[#E2E8F0] focus:border-[#1A365D]"
              data-testid="answer-textarea"
            />
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className="border-[#1A365D] text-[#1A365D]"
            data-testid="previous-button"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Précédent
          </Button>
          
          {currentIndex === questions.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#D4AF37] hover:bg-[#B3932E] text-[#0F172A]"
              data-testid="submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Évaluation en cours...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Terminer l'évaluation
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-[#1A365D] hover:bg-[#132745]"
              data-testid="next-button"
            >
              Suivant
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Question Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {questions.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-3 h-3 rounded-full transition-colors ${
                idx === currentIndex 
                  ? 'bg-[#1A365D]' 
                  : answers[questions[idx]?.id] 
                    ? 'bg-[#D4AF37]' 
                    : 'bg-[#E2E8F0]'
              }`}
              data-testid={`question-dot-${idx}`}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default DiagnosticPage;
